import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useEmployees } from '../context/EmployeeContext.jsx'
import { usePermissions } from '../context/PermissionContext.jsx'
import BulkImport from '../components/BulkImport.jsx'
import AIFilterChatbot from '../components/AIFilterChatbot.jsx'
import { getCookie } from '../utils/helpers.js'
import { TOKEN, getApiBaseUrl } from '@constants/constants'
import * as XLSX from 'xlsx'

// Import components
import WorkforceStats from '../components/Workforce/WorkforceStats.jsx'
import WorkforceActionBar from '../components/Workforce/WorkforceActionBar.jsx'
import WorkforceFilters from '../components/Workforce/WorkforceFilters.jsx'
import WorkforceTable from '../components/Workforce/WorkforceTable.jsx'
import EmployeeDetailsModal from '../components/Workforce/EmployeeDetailsModal.jsx'
import DeleteConfirmationModal from '../components/Workforce/DeleteConfirmationModal.jsx'
import LoadingSpinner from '../components/common/LoadingSpinner'

// Import custom hook
import { useWorkforce } from '../hooks/useWorkforce.js'

const scrollbarStyles = `
  .scrollbar-thin {
    scrollbar-width: thin;
  }
  .scrollbar-thin::-webkit-scrollbar { width: 6px; }
  .scrollbar-thin::-webkit-scrollbar-track { background: #f1f5f9; }
  .scrollbar-thin::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
  .scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
`

const EmployeeDirectory = ({ embedded = false }) => {
  const { deleteEmployee } = useEmployees()
  const { hasPermission, loading: permissionsLoading } = usePermissions()
  const navigate = useNavigate()
  
  const initialFilters = useMemo(() => ({
    parentDepartment: [], department: [], location: [], role: [],
    project: [], status: ['Active'], experience: [], skills: [], rates: []
  }), [])
  const initialPagination = { offset: 0, limit: 50, hasMore: true, totalCount: 0 }

  const {
    employees, loading, loadingMore, initialLoadComplete, fetchError, pagination, setPagination,
    searchTerm, setSearchTerm, debouncedSearchTerm, setDebouncedSearchTerm, filters, setFilters, debouncedFilters, setDebouncedFilters,
    sortConfig, handleSort, selectedTeamType, setSelectedTeamType, fixedTotalEmployeeCount, fetchFixedTotalEmployeeCount,
    weeklyAvgUtilization, weeklyUtilizationLoading, weeklyUtilizationError, fetchWeeklyAvgUtilization,
    fetchEmployees, clearFilters
  } = useWorkforce(initialFilters, initialPagination)

  const [showFilters, setShowFilters] = useState(false)
  const [showAIFilter, setShowAIFilter] = useState(false)
  const [aiFilteredEmployees, setAiFilteredEmployees] = useState(null)
  const [aiFilterLoading, setAiFilterLoading] = useState(false)
  const [filterOptions, setFilterOptions] = useState({ parentDepartments: [], departments: [], roles: [], locations: [], statuses: [] })
  const [filterCascading, setFilterCascading] = useState({ parent_department_departments: {}, parent_department_roles: {}, department_roles: {} })
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true)
  const [showMyTeamDropdown, setShowMyTeamDropdown] = useState(false)
  const myTeamDropdownRef = useRef(null)
  const [showBulkImportModal, setShowBulkImportModal] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [showEmployeeModal, setShowEmployeeModal] = useState(false)
  const [deletingEmployee, setDeletingEmployee] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editFormData, setEditFormData] = useState({})
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [employeeToDelete, setEmployeeToDelete] = useState(null)
  const [exportLoading, setExportLoading] = useState(false)
  const loadMoreTriggerRef = useRef(null)
  const [showContextMenu, setShowContextMenu] = useState(false)
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 })
  const [contextMenuEmployee, setContextMenuEmployee] = useState(null)

  // Local state for projects (pure API, no cache context)
  const [projects, setProjects] = useState([])
  const [allocationLoading, setAllocationLoading] = useState(false)
  const [allocationError, setAllocationError] = useState(null)

  const fetchAllocationsData = useCallback(async () => {
    const token = getCookie(TOKEN); if (!token) return
    setAllocationLoading(true); setAllocationError(null)
    try {
      const apiBaseUrl = getApiBaseUrl()
      const projRes = await fetch(`${apiBaseUrl}/api/projects/minimal`, { headers: { 'Authorization': `Bearer ${token}` } })
      
      if (projRes.ok) {
        const data = await projRes.json()
        setProjects(data.map(name => ({ name })))
      }
    } catch (e) {
      console.error('Error fetching projects:', e)
      setAllocationError('Error loading project list')
    } finally {
      setAllocationLoading(false)
    }
  }, [])

  const refreshDirectory = useCallback(() => {
    setPagination(prev => ({ ...prev, offset: 0, hasMore: true }))
    fetchEmployees(
      0, 
      pagination.limit, 
      debouncedSearchTerm, 
      debouncedFilters, 
      false, 
      selectedTeamType, 
      sortConfig.key, 
      sortConfig.direction
    )
    fetchFixedTotalEmployeeCount()
  }, [pagination.limit, debouncedSearchTerm, debouncedFilters, selectedTeamType, sortConfig, fetchEmployees, fetchFixedTotalEmployeeCount, setPagination])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500)
    return () => clearTimeout(timer)
  }, [searchTerm, setDebouncedSearchTerm])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 300)
    return () => clearTimeout(timer)
  }, [filters, setDebouncedFilters])

  // Combined effect for initial load and reactive updates (Search/Filter/Sort)
  useEffect(() => {
    if (permissionsLoading || !hasPermission('view-workforce') || aiFilteredEmployees) return
    refreshDirectory()
  }, [permissionsLoading, hasPermission, aiFilteredEmployees, refreshDirectory])

  // Effect for one-time metadata load
  useEffect(() => {
    if (!permissionsLoading && hasPermission('view-workforce')) {
      fetchWeeklyAvgUtilization()
      fetchAllocationsData()
    }
  }, [permissionsLoading, hasPermission, fetchWeeklyAvgUtilization, fetchAllocationsData])

  useEffect(() => {
    const fetchOptions = async () => {
      const token = getCookie(TOKEN); if (!token) { setFilterOptionsLoading(false); return }
      try {
        setFilterOptionsLoading(true)
        const apiBaseUrl = getApiBaseUrl()
        const [optsRes, cascRes] = await Promise.all([
          fetch(`${apiBaseUrl}/api/employees/filter-options`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${apiBaseUrl}/api/employees/filter-cascading`, { headers: { 'Authorization': `Bearer ${token}` } })
        ])
        
        if (optsRes.ok) {
          const d = await optsRes.json()
          setFilterOptions({
            parentDepartments: d.parent_departments || [],
            departments: d.departments || [],
            roles: d.designations || d.roles || [],
            locations: d.locations || [],
            statuses: d.statuses || []
          })
        }
        
        if (cascRes.ok) {
          const d = await cascRes.json()
          setFilterCascading({
            parent_department_departments: d.parent_department_departments || {},
            parent_department_roles: d.parent_department_roles || {},
            department_roles: d.department_roles || {}
          })
        }
      } catch (e) { console.error('Error fetching filter options:', e) } finally { setFilterOptionsLoading(false) }
    }
    if (!permissionsLoading && hasPermission('view-workforce')) fetchOptions()
  }, [permissionsLoading, hasPermission])

  useEffect(() => {
    if (!loadMoreTriggerRef.current || aiFilteredEmployees) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && pagination.hasMore && !loadingMore && !loading) {
          fetchEmployees(pagination.offset, pagination.limit, debouncedSearchTerm, debouncedFilters, true, selectedTeamType, sortConfig.key, sortConfig.direction)
        }
      },
      { rootMargin: '200px', threshold: 0.1 }
    )
    observer.observe(loadMoreTriggerRef.current)
    return () => observer.disconnect()
  }, [pagination.hasMore, loadingMore, loading, aiFilteredEmployees, pagination.offset, pagination.limit, debouncedSearchTerm, debouncedFilters, selectedTeamType, sortConfig, fetchEmployees])

  const handleApplyFilters = () => {
    setDebouncedFilters(filters)
    setShowFilters(false)
  }

  const handleClearFilters = () => {
    clearFilters() // Resets search, sort, and internal filters
    setShowFilters(false)
  }

  const getFilteredDepartments = useMemo(() => {
    const all = filterOptions.departments || []
    if (!filters.parentDepartment?.length) return all
    const allowed = new Set()
    filters.parentDepartment.forEach(pd => (filterCascading.parent_department_departments?.[pd] || []).forEach(d => allowed.add(d)))
    return allowed.size > 0 ? all.filter(d => allowed.has(d)) : []
  }, [filterOptions.departments, filters.parentDepartment, filterCascading.parent_department_departments])

  const getFilteredRoles = useMemo(() => {
    const all = filterOptions.roles || []
    if (filters.department?.length > 0) {
      const allowed = new Set()
      filters.department.forEach(dept => (filterCascading.department_roles?.[dept] || []).forEach(r => allowed.add(r)))
      return allowed.size > 0 ? all.filter(r => allowed.has(r)) : []
    }
    if (filters.parentDepartment?.length > 0) {
      const allowed = new Set()
      filters.parentDepartment.forEach(pd => (filterCascading.parent_department_roles?.[pd] || []).forEach(r => allowed.add(r)))
      return allowed.size > 0 ? all.filter(r => allowed.has(r)) : []
    }
    return all
  }, [filterOptions.roles, filters.department, filters.parentDepartment, filterCascading.department_roles, filterCascading.parent_department_roles])

  const activeFiltersCount = Object.entries(debouncedFilters).filter(([key, value]) => {
    if (key === 'status') return Array.isArray(value) && value.length > 0 && !(value.length === 1 && value.includes('Active'))
    return Array.isArray(value) ? value.length > 0 : value !== ''
  }).length + (selectedTeamType ? 1 : 0)

  const filteredAndSortedEmployees = useMemo(() => {
    if (aiFilterLoading) return []
    let filtered = aiFilteredEmployees || employees || []
    if (aiFilteredEmployees && searchTerm?.trim()) {
      const s = searchTerm.toLowerCase().trim()
      filtered = filtered.filter(emp => {
        const fn = (emp.first_name || emp.firstName || '').toLowerCase()
        const ln = (emp.last_name || emp.lastName || '').toLowerCase()
        const em = (emp.email || '').toLowerCase()
        const id = (emp.employee_id || emp.employeeId || emp.id || '').toString().toLowerCase()
        return fn.includes(s) || ln.includes(s) || em.includes(s) || id.includes(s) || `${fn} ${ln}`.includes(s)
      })
    }
    return filtered
  }, [employees, searchTerm, aiFilteredEmployees, aiFilterLoading])

  const handleExport = async () => {
    try {
      setExportLoading(true)
      const token = getCookie(TOKEN); if (!token) return
      const params = new URLSearchParams()
      if (debouncedSearchTerm?.trim()) params.append('search', debouncedSearchTerm.trim())
      if (debouncedFilters.parentDepartment?.length > 0) params.append('parent_department', debouncedFilters.parentDepartment.join(','))
      if (debouncedFilters.department?.length > 0) params.append('department', debouncedFilters.department.join(','))
      if (debouncedFilters.location?.length > 0) params.append('location', debouncedFilters.location.join(','))
      if (debouncedFilters.role?.length > 0) params.append('designation', debouncedFilters.role.join(','))
      if (debouncedFilters.status?.length > 0) params.append('status', debouncedFilters.status.join(','))
      if (debouncedFilters.experience?.length > 0) params.append('experience', debouncedFilters.experience.join(','))
      if (debouncedFilters.project?.length > 0) params.append('project', debouncedFilters.project.join(','))
      if (selectedTeamType) params.append('team_filter', selectedTeamType)
      
      const res = await fetch(`${getApiBaseUrl()}/api/employees?${params.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } })
      if (!res.ok) throw new Error('Export failed')
      const data = await res.json()
      const exportData = (data.employees || []).map(emp => ({
        'Employee ID': emp.employee_id || emp.id || '',
        'Name': `${emp.first_name || ''} ${emp.last_name || ''}`,
        'Email': emp.email || '',
        'Department': emp.department || '',
        'Role': emp.designation || emp.title || '',
        'Status': emp.employee_status || 'Active'
      }))
      const ws = XLSX.utils.json_to_sheet(exportData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Employees')
      XLSX.writeFile(wb, `employees_export_${new Date().toISOString().split('T')[0]}.xlsx`)
    } catch (e) { alert('Export error') } finally { setExportLoading(false) }
  }

  const formatExperience = (exp) => {
    if (!exp || exp === 0) return 'N/A'
    if (typeof exp === 'string') return exp
    const v = Number(exp); if (isNaN(v)) return 'N/A'
    if (v < 1.0) { const m = Math.round(v * 100); return `${m} month${m !== 1 ? 's' : ''}` }
    return `${v.toFixed(2)} years`
  }

  const handleAIFilterApply = async (aiFilters) => {
    if (!aiFilters?.length) { setAiFilteredEmployees(null); setAiFilterLoading(false); return }
    setAiFilterLoading(true); setAiFilteredEmployees(null); setSelectedTeamType(null)
    try {
      const token = getCookie(TOKEN); if (!token) return
      const res = await fetch(`${getApiBaseUrl()}/api/ai/filter-apply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters: aiFilters, tableName: "employees" })
      })
      if (res.ok) { const d = await res.json(); setAiFilteredEmployees(d.data || []); setShowFilters(false) }
    } catch (e) { console.error(e) } finally { setAiFilterLoading(false) }
  }

  const handleRowClick = (emp) => {
    setSelectedEmployee(emp); setShowEmployeeModal(true); setIsEditing(false)
    setEditFormData({
      firstName: emp.firstName || emp.first_name || '', lastName: emp.lastName || emp.last_name || '',
      email: emp.email || '', phone: emp.phone || emp.mobile_number || '',
      dateOfBirth: (emp.date_of_birth || '').split('T')[0], title: emp.title || emp.designation || '',
      department: emp.department || '', location: emp.location || emp.city || '',
      employmentType: emp.employment_type || 'Full-time', hireDate: (emp.date_of_joining || '').split('T')[0],
      employeeStatus: emp.employee_status || 'Active'
    })
  }

  const confirmDeleteEmployee = async () => {
    if (!employeeToDelete) return
    setShowDeleteConfirm(false); setDeletingEmployee(employeeToDelete.id)
    try {
      const res = await deleteEmployee(employeeToDelete.id)
      if (res.success) {
        setShowEmployeeModal(false); setSelectedEmployee(null)
        refreshDirectory()
      } else alert('Delete failed')
    } catch (e) { console.error(e) } finally { setDeletingEmployee(null); setEmployeeToDelete(null) }
  }

  const getEmployeeCurrentProjects = useCallback((id) => {
    const employee = employees.find(emp => emp.id === id)
    return employee?.current_projects || []
  }, [employees])

  const handleRightClick = (e, emp) => { e.preventDefault(); e.stopPropagation(); setContextMenuPosition({ x: e.clientX, y: e.clientY }); setContextMenuEmployee(emp); setShowContextMenu(true) }
  const handleViewProfile = () => { if (contextMenuEmployee) navigate(`/employee-profile/${contextMenuEmployee.id || contextMenuEmployee.employee_id}`); setShowContextMenu(false) }

  return (
    <div className={embedded ? "" : "max-w-[97%] mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
      <style>{scrollbarStyles}</style>
      {permissionsLoading && <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>}
      
      {!permissionsLoading && hasPermission('view-workforce') && (
        <>
          {!embedded && (
            <div className="mb-8 animate-fade-in">
              <h1 className="page-title text-xl">Employee Directory</h1>
              <p className="page-subtitle">Manage and search your organization's workforce</p>
            </div>
          )}

          <WorkforceStats 
            fixedTotalEmployeeCount={fixedTotalEmployeeCount}
            loading={loading}
            weeklyAvgUtilization={weeklyAvgUtilization}
            weeklyUtilizationLoading={weeklyUtilizationLoading}
            weeklyUtilizationError={weeklyUtilizationError}
          />

          <div className="card p-6 animate-slide-in">
            <WorkforceActionBar 
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onSearchClear={() => setSearchTerm('')}
              showMyTeamDropdown={showMyTeamDropdown}
              onMyTeamDropdownToggle={() => setShowMyTeamDropdown(!showMyTeamDropdown)}
              selectedTeamType={selectedTeamType}
              onTeamTypeSelect={(type) => { setSelectedTeamType(type); setShowMyTeamDropdown(false) }}
              myTeamDropdownRef={myTeamDropdownRef}
              showFilters={showFilters}
              onFiltersToggle={() => setShowFilters(!showFilters)}
              activeFiltersCount={activeFiltersCount}
              showAIFilter={showAIFilter}
              onAIFilterToggle={() => setShowAIFilter(!showAIFilter)}
              aiFilteredEmployees={aiFilteredEmployees}
              hasPermission={hasPermission}
              onBulkImportOpen={() => setShowBulkImportModal(true)}
              onExport={handleExport}
              exportLoading={exportLoading}
            />

            <WorkforceFilters 
              showFilters={showFilters}
              onFiltersClose={handleApplyFilters}
              filterOptions={filterOptions}
              filterOptionsLoading={filterOptionsLoading}
              filters={filters}
              onFiltersChange={(key, val) => setFilters(prev => ({ ...prev, [key]: val }))}
              getFilteredDepartments={getFilteredDepartments}
              getFilteredRoles={getFilteredRoles}
              uniqueLocations={filterOptions.locations}
              uniqueStatuses={filterOptions.statuses}
              projects={projects}
              allocationLoading={allocationLoading}
              onClearFilters={handleClearFilters}
            />

            {loading || aiFilterLoading || !initialLoadComplete ? (
              <LoadingSpinner />
            ) : fetchError ? (
              <div className="text-center py-12 text-red-600">{fetchError}</div>
            ) : filteredAndSortedEmployees.length === 0 ? (
              <div className="text-center py-12 text-slate-500">No employees found</div>
            ) : (
              <WorkforceTable 
                employees={filteredAndSortedEmployees}
                sortConfig={sortConfig}
                onSort={handleSort}
                onRowClick={handleRowClick}
                onRightClick={handleRightClick}
                expandedRows={expandedRows}
                onToggleExpand={(id) => {
                  const next = new Set(expandedRows)
                  if (next.has(id)) next.delete(id); else next.add(id)
                  setExpandedRows(next)
                }}
                allocationLoading={allocationLoading}
                allocationError={allocationError}
                getEmployeeCurrentProjects={getEmployeeCurrentProjects}
                formatExperience={formatExperience}
                hasPermission={hasPermission}
                loadMoreTriggerRef={loadMoreTriggerRef}
                pagination={pagination}
                loadingMore={loadingMore}
              />
            )}
          </div>
        </>
      )}

      {showBulkImportModal && (
        <BulkImport 
          onClose={() => setShowBulkImportModal(false)}
          onImportComplete={() => {
            setShowBulkImportModal(false)
            refreshDirectory()
          }}
        />
      )}

      <EmployeeDetailsModal 
        isOpen={showEmployeeModal}
        onClose={() => setShowEmployeeModal(false)}
        employee={selectedEmployee}
        isEditing={isEditing}
        onEdit={() => setIsEditing(true)}
        onCancelEdit={() => setIsEditing(false)}
        onInputChange={(f, v) => setEditFormData(p => ({ ...p, [f]: v }))}
        editFormData={editFormData}
        uniqueDepartments={filterOptions.departments}
        uniqueLocations={filterOptions.locations}
        formatExperience={formatExperience}
        isDeleting={deletingEmployee === selectedEmployee?.id}
        hasPermission={hasPermission}
        onDelete={(id, name) => { setEmployeeToDelete({ id, name }); setShowDeleteConfirm(true) }}
        onEditNavigation={(emp) => navigate(`/employee/${emp.id || emp.employee_id}/edit`, { state: { employee: emp } })}
      />

      <DeleteConfirmationModal 
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteEmployee}
        employeeName={employeeToDelete?.name}
        isDeleting={deletingEmployee !== null}
      />

      {showAIFilter && (
        <AIFilterChatbot 
          onClose={() => setShowAIFilter(false)}
          onApplyFilters={handleAIFilterApply}
          tableName="employees"
        />
      )}

      {showContextMenu && contextMenuEmployee && (
        <div 
          className="context-menu fixed bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50 min-w-[160px]"
          style={{ left: contextMenuPosition.x, top: contextMenuPosition.y }}
        >
          <button onClick={handleViewProfile} className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2">
            View Profile
          </button>
        </div>
      )}
    </div>
  )
}

export default EmployeeDirectory
